import type { PlanningInsightItemDto } from "../../../api/planningInsights";
import type { RaidInsightItemDto } from "../../../api/raid";
import { resolveRaidCriticality } from "../../../utils/raidCriticality";
import { ClientTable, Tabs, type TabsItem } from "../../shared";
import {
  BulletList,
  ConfidenceBadge,
  DetailAccordionGroup,
  DetailAccordionSection,
  DetailKeyMessage,
  type DetailMetadataItem,
  DetailMetadataStrip,
  DetailModalShell,
  DetailStatusBadge,
  DetailSubsection,
  DueDateWithCountdown,
  EvidenceReferenceList,
  LinkedItems,
  PriorityBadge,
  StatusBadge,
} from "../AiInsightsRaidDetailParts";
import type { RaidRisksIssuesTopItemDto } from "./AiInsightsRaidRisksIssuesTypes";
import { formatRaidInsightDaysLeft, formatRaidInsightDueDate } from "./raidInsightDisplay";

export type AiInsightsRaidRisksIssuesDetailModalProps = {
  item: RaidRisksIssuesTopItemDto;
  sourceItem?: RaidInsightItemDto;
  asAtDate?: string | null;
  resolvedItemsById?: ReadonlyMap<string, RaidInsightItemDto>;
  resolvedPlanningItemsById?: ReadonlyMap<string, PlanningInsightItemDto>;
  onClose: () => void;
};

interface RiskOverviewRow {
  riskId: string;
  workstream: string;
  criticality: string;
  status: string;
}

interface ImpactScopeRow {
  category: string;
  affectedItem: string;
}

function firstValue(...values: Array<string | null | undefined>) {
  return values.find((value) => value != null && value.trim().length > 0) ?? null;
}

export function AiInsightsRaidRisksIssuesDetailModal({
  item,
  sourceItem,
  asAtDate,
  resolvedItemsById,
  resolvedPlanningItemsById,
  onClose,
}: AiInsightsRaidRisksIssuesDetailModalProps) {
  const idHeader = sourceItem?.raidType?.toLowerCase() === "issue" ? "Issue ID" : "Risk ID";
  const overviewRow: RiskOverviewRow = {
    riskId: sourceItem?.itemId ?? item.item_id ?? "Source item unavailable",
    workstream: sourceItem?.workstream ?? "Not available",
    criticality:
      resolveRaidCriticality(sourceItem?.criticality) ?? item.criticality ?? "Not available",
    status: sourceItem?.status ?? "Not available",
  };
  const causeFactors = Array.isArray(item.cause_analysis?.evidence_supported_contributing_factors)
    ? item.cause_analysis.evidence_supported_contributing_factors
    : [];
  const unknowns = Array.isArray(item.cause_analysis?.unknowns) ? item.cause_analysis.unknowns : [];
  const causeAnalysisItems = [
    item.cause_analysis?.recorded_cause,
    ...causeFactors.map((factor) => factor.factor),
    ...unknowns.map((unknown) => `Unknown: ${unknown}`),
  ].filter((cause): cause is string => cause != null && cause.trim().length > 0);
  const controlOwner = firstValue(sourceItem?.owner);
  const controlStatus = firstValue(sourceItem?.status);
  const controlDueDate = firstValue(sourceItem?.dueDate);
  const controlStatusProvenance: DetailMetadataItem["provenance"] =
    sourceItem?.status != null
      ? "Source record"
      : undefined;
  const calculatedDaysUntilDueDate = formatRaidInsightDaysLeft(controlDueDate, asAtDate);
  const daysUntilDueDate =
    calculatedDaysUntilDueDate !== "Not available"
      ? calculatedDaysUntilDueDate
      : "Not available";
  const affectedWorkstreams = Array.isArray(item.impact_analysis?.affected_workstreams)
    ? item.impact_analysis.affected_workstreams
    : [];
  const affectedMilestones = Array.isArray(item.impact_analysis?.affected_milestones_or_activities)
    ? item.impact_analysis.affected_milestones_or_activities
    : [];
  const affectedObjectives = Array.isArray(item.impact_analysis?.affected_objectives_or_value)
    ? item.impact_analysis.affected_objectives_or_value
    : [];
  const linkedItems = Array.isArray(item.linked_items) ? item.linked_items : [];
  const evidenceIds = Array.isArray(item.evidence_ids) ? item.evidence_ids : [];
  const impactScopeRows: ImpactScopeRow[] = [
    {
      category: "Workstreams",
      affectedItem: affectedWorkstreams.join("; "),
    },
    {
      category: "Milestones or activities",
      affectedItem: affectedMilestones.join("; "),
    },
    {
      category: "Objectives or value",
      affectedItem: affectedObjectives.join("; "),
    },
  ].filter((row) => row.affectedItem.trim().length > 0);
  const whyThisMattersItems = [
    item.why_it_is_important_now,
    item.why_priority_for_this_user,
  ].filter((message): message is string => message != null && message.trim().length > 0);
  const recommendedNextSteps = Array.isArray(item.recommended_next_steps)
    ? item.recommended_next_steps
    : item.recommended_focus != null
      ? [item.recommended_focus]
      : [];
  const impactSummary = firstValue(
    item.impact_analysis?.potential_impact,
    item.impact_analysis?.observed_impact
  );
  const controlMetadataItems: DetailMetadataItem[] = [
    {
      label: "Owner",
      value: controlOwner ?? "Not available",
      provenance:
        sourceItem?.owner != null
          ? "Source record"
          : undefined,
    },
    ...(controlStatus !== overviewRow.status
      ? [
          {
            label: "Status",
            value: <StatusBadge value={controlStatus} />,
            provenance: controlStatusProvenance,
          },
        ]
      : []),
    {
      label: "Due date",
      value: (
        <DueDateWithCountdown
          dueDate={formatRaidInsightDueDate(controlDueDate)}
          daysUntilDueDate={daysUntilDueDate}
        />
      ),
      provenance:
        sourceItem?.dueDate != null
          ? "Source record"
          : undefined,
    },
  ];

  return (
    <DetailModalShell
      title={sourceItem?.title ?? "Source item unavailable"}
      onClose={onClose}
      className="global-insights-overlay__risk-detail-modal--risk-issue"
      lastUpdatedAt={sourceItem?.lastUpdatedAt}
    >
      <DetailMetadataStrip
        items={[
          {
            label: idHeader,
            value: overviewRow.riskId,
            provenance: sourceItem?.itemId != null ? "Source record" : "AI assessed",
          },
          { label: "Workstream", value: overviewRow.workstream, provenance: "Source record" },
          {
            label: "Criticality",
            value: (
              <span className="global-insights-overlay__ai-source-value">
                <PriorityBadge value={overviewRow.criticality} />
              </span>
            ),
            provenance:
              resolveRaidCriticality(sourceItem?.criticality) != null
                ? "Source record"
                : item.criticality != null
                  ? "AI assessed"
                  : undefined,
          },
          {
            label: "Status",
            value: <StatusBadge value={overviewRow.status} />,
            provenance: sourceItem?.status != null ? "Source record" : undefined,
          },
          {
            label: "Confidence",
            value: (
              <span className="global-insights-overlay__ai-source-value">
                <ConfidenceBadge value={item.confidence} />
              </span>
            ),
            provenance: "AI assessed",
          },
        ]}
      />

      <DetailKeyMessage
        label={
          item.impact_analysis?.potential_impact != null
            ? "Potential impact"
            : item.impact_analysis?.observed_impact != null
              ? "Observed impact"
              : "Impact summary"
        }
      >
        {impactSummary ?? "Not available"}
      </DetailKeyMessage>

      <Tabs
        variant="underline"
        className="global-insights-overlay__risk-detail-tabs"
        items={
          [
            {
              key: "analysis",
              label: "Analysis",
              content: (
                <DetailAccordionGroup>
                  <DetailAccordionSection
                    heading="Impact analysis"
                    aiGenerated
                    itemKey="impact-analysis"
                  >
                    <div className="global-insights-overlay__risk-detail-impact-summary">
                      {item.impact_analysis?.observed_impact !== impactSummary && (
                        <DetailSubsection label="Observed impact">
                          <p className="global-insights-overlay__risk-detail-text">
                            {item.impact_analysis?.observed_impact ?? "Not available"}
                          </p>
                        </DetailSubsection>
                      )}
                      {item.impact_analysis?.potential_impact !== impactSummary && (
                        <DetailSubsection label="Potential future impact">
                          <p className="global-insights-overlay__risk-detail-text">
                            {item.impact_analysis?.potential_impact ?? "Not available"}
                          </p>
                        </DetailSubsection>
                      )}
                    </div>
                    <DetailSubsection label="Affected scope">
                      {impactScopeRows.length > 0 ? (
                        <ClientTable
                          className="global-insights-overlay__risk-detail-table"
                          data={impactScopeRows}
                          rowKey={(row) => `${row.category}-${row.affectedItem}`}
                          columns={[
                            { id: "category", header: "Area", accessor: "category" },
                            {
                              id: "affectedItem",
                              header: "Affected item",
                              accessor: "affectedItem",
                            },
                          ]}
                          condensed
                        />
                      ) : (
                        <p className="global-insights-overlay__risk-detail-text">
                          No affected areas identified.
                        </p>
                      )}
                    </DetailSubsection>
                  </DetailAccordionSection>
                  <DetailAccordionSection
                    heading="Cause analysis"
                    aiGenerated
                    itemKey="cause-analysis"
                    statusBadge={
                      unknowns.length > 0 ? <DetailStatusBadge value="Review needed" /> : null
                    }
                  >
                    <BulletList items={causeAnalysisItems} />
                  </DetailAccordionSection>
                  <DetailAccordionSection
                    heading="Why this matters now"
                    aiGenerated
                    itemKey="why-matters"
                  >
                    <DetailSubsection label="Time sensitivity">
                      <p className="global-insights-overlay__risk-detail-text">
                        {item.time_sensitivity ?? "Not available"}
                      </p>
                    </DetailSubsection>
                    <BulletList items={whyThisMattersItems} />
                  </DetailAccordionSection>
                </DetailAccordionGroup>
              ),
            },
            {
              key: "response",
              label: "Response",
              content: (
                <DetailAccordionGroup>
                  <DetailAccordionSection
                    heading="Mitigation and control"
                    itemKey="mitigation-control"
                  >
                    <DetailMetadataStrip inline items={controlMetadataItems} />
                    <DetailSubsection label="AI mitigation and control assessment">
                      <p className="global-insights-overlay__risk-detail-text">
                        {item.mitigation_and_control_assessment ?? "Not available"}
                      </p>
                    </DetailSubsection>
                  </DetailAccordionSection>
                  <DetailAccordionSection
                    heading="Recommended next steps"
                    itemKey="recommended-next-steps"
                  >
                    <BulletList items={recommendedNextSteps} />
                  </DetailAccordionSection>
                </DetailAccordionGroup>
              ),
            },
            {
              key: "evidence",
              label: `Evidence & links${linkedItems.length + evidenceIds.length > 0 ? ` (${linkedItems.length + evidenceIds.length})` : ""}`,
              content: (
                <DetailAccordionGroup>
                  <DetailAccordionSection
                    heading="Linked items"
                    itemKey="linked-items"
                    badge={linkedItems.length}
                  >
                    <LinkedItems items={linkedItems} resolvedItemsById={resolvedItemsById} />
                  </DetailAccordionSection>
                  <DetailAccordionSection
                    heading="Supporting evidence"
                    itemKey="supporting-evidence"
                    badge={evidenceIds.length}
                  >
                    <EvidenceReferenceList
                      items={evidenceIds}
                      getTo={(id) => {
                        const stableItemId = resolvedPlanningItemsById?.get(id.toLowerCase())
                          ?.stableItemId?.trim();
                        return stableItemId == null || stableItemId.length === 0
                          ? undefined
                          : `/integration-planning?focusId=${encodeURIComponent(stableItemId)}&tab=detailed-view`;
                      }}
                      getAriaLabel={(id) => {
                        const title = resolvedPlanningItemsById
                          ?.get(id.toLowerCase())
                          ?.title?.trim();
                        return title ? `Go to this item: ${title}` : undefined;
                      }}
                    />
                  </DetailAccordionSection>
                </DetailAccordionGroup>
              ),
            },
          ] satisfies TabsItem[]
        }
      />
    </DetailModalShell>
  );
}

export default AiInsightsRaidRisksIssuesDetailModal;
